import type {
  CanvasState,
  ChatFrame,
  CodeFrame,
  Command,
  Effect,
  Frame,
  FreeFrame,
  FxFrame,
  InstanceFrame,
  MainFrame,
  NoteFrame,
  StyleguideFrame,
  ThemeToken,
  ThemeModeTokens,
  TsxFreeFrame,
  TsxMainFrame,
  TsxInstanceFrame,
  MotionTimeline,
  MotionTrack,
} from './types';
import { isPanelOwned, lightTokens } from './types';
import {
  emptyTimeline,
  setLayerBlend as mSetLayerBlend,
  setKeyframe as mSetKeyframe,
  moveKeyframe as mMoveKeyframe,
  removeKeyframe as mRemoveKeyframe,
  setEase as mSetEase,
  setDuration as mSetDuration,
  setPlayback as mSetPlayback,
  removeTrack as mRemoveTrack,
} from '../motion/model';
import { normalizeProps, readPropsManifest } from './props-schema';
import { extractSlotSchemas, injectCfNodeIds, resolveCfNode } from './slot-schema';
import { editNodeText, setNodeMediaRef, setLottieSlotsAttr, deleteNode, duplicateNode, moveNode, setNodeInnerHtml, insertNodeHtml } from './editNode';
import { resolveInstance } from './resolve-instance';
import { isolateStyles } from './isolate-styles';
import { parseDesignMd } from '../style/designmd';

export const ZOOM_MIN = 0.2;
export const ZOOM_MAX = 4;

/**
 * Expand a delete set to also remove panel-owned tool frames anchored to any
 * deleted frame — they're hidden Editor Panel children that would otherwise
 * orphan. Used by BOTH the reducer and inverse() so undo restores symmetrically.
 */
export function cascadeDeleteIds(frames: Frame[], ids: Iterable<string>): Set<string> {
  const out = new Set(ids);
  for (const f of frames) {
    if (isPanelOwned(f) && f.anchorId && out.has(f.anchorId)) out.add(f.id);
  }
  return out;
}

const NONE_EFFECT: Effect = { kind: 'none' };

export function emptyState(): CanvasState {
  return {
    doc: { version: 2, frames: [], viewport: { pan: { x: 0, y: 0 }, zoom: 1 } },
    selection: [],
  };
}

function nextZ(frames: Frame[]): number {
  return frames.reduce((m, f) => Math.max(m, f.z), 0) + 1;
}

export function applyCommand(s: CanvasState, c: Command): CanvasState {
  switch (c.t) {
    case 'frame.create': {
      const frame: FreeFrame = {
        kind: 'free',
        id: c.id,
        x: c.x,
        y: c.y,
        w: c.w,
        z: nextZ(s.doc.frames),
        title: c.title,
        html: c.html,
        effect: NONE_EFFECT,
      };
      return { ...s, doc: { ...s.doc, frames: [...s.doc.frames, frame] } };
    }
    case 'frame.updateHtml':
      return mapFrames(s, (f) => {
        if (f.id !== c.id) return f;
        // Persist cf-ids on free frames too (not just main) so motion targets
        // survive html edits — injectCfNodeIds preserves any existing ids.
        if (f.kind === 'free') return { ...f, html: injectCfNodeIds(c.html) };
        if (f.kind === 'main') {
          const htmlWithIds = injectCfNodeIds(c.html);
          return { ...f, html: htmlWithIds, slots: extractSlotSchemas(htmlWithIds) };
        }
        // instance: no own html
        return f;
      });
    case 'frame.editNode':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        if (f.kind === 'free') {
          // stamp so cf-ids match the render seam, mutate, keep stamped html
          return { ...f, html: editNodeText(injectCfNodeIds(f.html), c.cfNodeId, c.text) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(editNodeText(injectCfNodeIds(f.html), c.cfNodeId, c.text));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f; // instance text is Main-owned / slot-routed by the UI
      });
    case 'frame.setNodeMedia':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        if (f.kind === 'free') {
          return { ...f, html: setNodeMediaRef(injectCfNodeIds(f.html), c.cfNodeId, c.ref) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(setNodeMediaRef(injectCfNodeIds(f.html), c.cfNodeId, c.ref));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f;
      });
    case 'lottie.setSlots':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        if (f.kind === 'free') {
          return { ...f, html: setLottieSlotsAttr(injectCfNodeIds(f.html), c.cfNodeId, c.slots) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(setLottieSlotsAttr(injectCfNodeIds(f.html), c.cfNodeId, c.slots));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f;
      });
    case 'frame.deleteNode':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        if (f.kind === 'free') {
          return { ...f, html: deleteNode(injectCfNodeIds(f.html), c.cfNodeId) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(deleteNode(injectCfNodeIds(f.html), c.cfNodeId));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f; // instance structure is Main-owned
      });
    case 'frame.duplicateNode':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        // Re-inject after duplicating so the clone gets a fresh cf-id (the copy
        // inherits the source id until re-stamped).
        if (f.kind === 'free') {
          return { ...f, html: injectCfNodeIds(duplicateNode(injectCfNodeIds(f.html), c.cfNodeId)) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(duplicateNode(injectCfNodeIds(f.html), c.cfNodeId));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f;
      });
    case 'frame.moveNode':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        if (f.kind === 'free') {
          // moveNode adds no nodes (the moved node keeps its id), so a single inject
          // — to resolve the targets — suffices, matching frame.deleteNode.
          return { ...f, html: moveNode(injectCfNodeIds(f.html), c.cfNodeId, c.refCfNodeId, c.position) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(moveNode(injectCfNodeIds(f.html), c.cfNodeId, c.refCfNodeId, c.position));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f; // instance structure is Main-owned
      });
    case 'frame.setNodeHtml':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        if (f.kind === 'free') {
          return { ...f, html: injectCfNodeIds(setNodeInnerHtml(injectCfNodeIds(f.html), c.cfNodeId, c.html)) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(setNodeInnerHtml(injectCfNodeIds(f.html), c.cfNodeId, c.html));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f;
      });
    case 'frame.insertNode':
      return mapFrames(s, (f) => {
        if (f.id !== c.frameId) return f;
        if (f.kind === 'free') {
          return { ...f, html: injectCfNodeIds(insertNodeHtml(injectCfNodeIds(f.html), c.refCfNodeId, c.html, c.position)) };
        }
        if (f.kind === 'main') {
          const next = injectCfNodeIds(insertNodeHtml(injectCfNodeIds(f.html), c.refCfNodeId, c.html, c.position));
          return { ...f, html: next, slots: extractSlotSchemas(next) };
        }
        return f;
      });
    case 'frame.setEffect':
      return mapFrames(s, (f) => (f.id === c.id ? { ...f, effect: c.effect } : f));
    case 'frame.setScript':
      return mapFrames(s, (f) =>
        f.id === c.id && (f.kind === 'free' || f.kind === 'main') ? { ...f, script: c.script } : f,
      );
    case 'frame.setTheme':
      return mapFrames(s, (f) => (f.id === c.id ? { ...f, themeId: c.themeId } : f));
    case 'theme.create': {
      // Upsert by id: re-creating an existing theme overwrites it rather than
      // appending a duplicate (duplicate ids corrupt find()/cascade resolution).
      const without = (s.doc.themes ?? []).filter((t) => t.id !== c.theme.id);
      return { ...s, doc: { ...s.doc, themes: [...without, c.theme] } };
    }
    case 'theme.update':
      return {
        ...s,
        doc: {
          ...s.doc,
          themes: (s.doc.themes ?? []).map((t) => (t.id === c.id ? mergeTheme(t, c.patch) : t)),
        },
      };
    case 'theme.delete':
      return { ...s, doc: { ...s.doc, themes: (s.doc.themes ?? []).filter((t) => t.id !== c.id) } };
    case 'theme.importDesignMd': {
      const theme = parseDesignMd(c.source);
      const themes = (s.doc.themes ?? []).filter((t) => t.id !== theme.id);
      return { ...s, doc: { ...s.doc, themes: [...themes, theme] } };
    }
    case 'doc.setActiveTheme':
      return { ...s, doc: { ...s.doc, activeThemeId: c.themeId } };
    case 'tsxFrame.create': {
      const frame: TsxFreeFrame = {
        kind: 'tsxFree',
        id: c.id, x: c.x, y: c.y, w: c.w, h: c.h, z: nextZ(s.doc.frames),
        title: c.title, effect: NONE_EFFECT, tsx: c.tsx,
      };
      return { ...s, doc: { ...s.doc, frames: [...s.doc.frames, frame] } };
    }
    case 'tsx.setSource':
      return mapFrames(s, (f) => {
        if (f.id !== c.id) return f;
        if (f.kind === 'tsxFree') return { ...f, tsx: c.tsx };
        if (f.kind === 'tsxMain') {
          const raw = readPropsManifest(c.tsx);
          const propsSchema = raw === undefined ? f.propsSchema : normalizeProps(raw);
          return { ...f, tsx: c.tsx, propsSchema };
        }
        return f;
      });
    case 'tsxFrame.promoteToMain':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'tsxFree') return f;
        const main: TsxMainFrame = {
          kind: 'tsxMain',
          id: f.id, x: f.x, y: f.y, w: f.w, h: f.h, z: f.z,
          title: f.title, effect: f.effect,
          tsx: f.tsx, propsSchema: normalizeProps(readPropsManifest(f.tsx)),
          locked: c.locked ?? false,
        };
        return main;
      });
    case 'tsxInstance.create': {
      const main = s.doc.frames.find((f) => f.id === c.mainId);
      if (!main || main.kind !== 'tsxMain') return s;
      const inst: TsxInstanceFrame = {
        kind: 'tsxInstance',
        id: c.id, mainId: c.mainId,
        x: c.x, y: c.y, w: c.w ?? main.w, h: main.h, z: nextZ(s.doc.frames),
        title: main.title, effect: NONE_EFFECT,
        propValues: { ...c.propValues },
      };
      return { ...s, doc: { ...s.doc, frames: [...s.doc.frames, inst] } };
    }
    case 'tsxInstance.setProp':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'tsxInstance') return f;
        return { ...f, propValues: { ...f.propValues, [c.name]: c.value } };
      });
    case 'tsxInstance.resetProp':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'tsxInstance') return f;
        const { [c.name]: _drop, ...rest } = f.propValues;
        return { ...f, propValues: rest };
      });
    case 'doc.clear':
      return { ...s, doc: emptyState().doc, selection: [] };
    case 'frame.restore':
      // Restore content from the snapshot, keep the live frame's geometry so it
      // doesn't jump on the canvas — live geometry wins, falling back to the
      // snapshot's height if the live frame has none. id matches (snapshot.id === live.id).
      return mapFrames(s, (f) =>
        f.id === c.frame.id ? ({ ...c.frame, x: f.x, y: f.y, z: f.z, w: f.w, h: f.h ?? c.frame.h } as Frame) : f);
    case 'doc.restore': {
      // Replace the whole doc, but keep live panel-owned frames (chat/editor
      // state) which never belong to a snapshot. Reset selection — old ids may
      // reference frames the snapshot doesn't contain.
      const panelOwned = s.doc.frames.filter(isPanelOwned);
      return { ...s, selection: [], doc: { ...c.doc, frames: [...c.doc.frames.filter((f) => !isPanelOwned(f)), ...panelOwned] } };
    }
    case 'frame.move': {
      const ids = new Set(c.ids);
      return mapFrames(s, (f) =>
        ids.has(f.id) ? { ...f, x: f.x + c.dx, y: f.y + c.dy } : f,
      );
    }
    case 'frame.resize':
      return mapFrames(s, (f) =>
        f.id === c.id
          ? { ...f, w: c.w, h: c.h, ...(c.x !== undefined ? { x: c.x } : {}), ...(c.y !== undefined ? { y: c.y } : {}) }
          : f,
      );
    case 'frame.delete': {
      const ids = cascadeDeleteIds(s.doc.frames, c.ids);
      return {
        ...s,
        doc: { ...s.doc, frames: s.doc.frames.filter((f) => !ids.has(f.id)) },
        selection: s.selection.filter((id) => !ids.has(id)),
      };
    }
    case 'select':
      return { ...s, selection: [...c.ids] };
    case 'viewport.focus': {
      const { bounds } = c;
      const pad = 80;
      const winW = typeof window !== 'undefined' ? window.innerWidth : 1280;
      const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
      const zoom = clamp(
        Math.min((winW - pad * 2) / bounds.w, (winH - pad * 2) / bounds.h),
        ZOOM_MIN,
        ZOOM_MAX,
      );
      const pan = {
        x: winW / 2 - (bounds.x + bounds.w / 2) * zoom,
        y: winH / 2 - (bounds.y + bounds.h / 2) * zoom,
      };
      return { ...s, doc: { ...s.doc, viewport: { pan, zoom } } };
    }
    case 'viewport.center': {
      // Pan so bounds center sits at the viewport center; zoom unchanged.
      const { bounds } = c;
      const { zoom } = s.doc.viewport;
      const winW = typeof window !== 'undefined' ? window.innerWidth : 1280;
      const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
      const pan = {
        x: winW / 2 - (bounds.x + bounds.w / 2) * zoom,
        y: winH / 2 - (bounds.y + bounds.h / 2) * zoom,
      };
      return { ...s, doc: { ...s.doc, viewport: { pan, zoom } } };
    }
    case 'viewport.set':
      return {
        ...s,
        doc: { ...s.doc, viewport: { pan: c.pan, zoom: clamp(c.zoom, ZOOM_MIN, ZOOM_MAX) } },
      };
    case 'frame.promoteToMain':
      return mapFrames(s, (f) => {
        if (f.id !== c.id) return f;
        if (f.kind === 'main') return f; // already a main → no-op
        if (f.kind !== 'free') return f; // can't promote an instance
        const htmlWithIds = injectCfNodeIds(f.html);
        const slots = extractSlotSchemas(htmlWithIds);
        const main: MainFrame = {
          kind: 'main',
          id: f.id, x: f.x, y: f.y, w: f.w, h: f.h, z: f.z,
          title: f.title, effect: f.effect,
          html: htmlWithIds, slots, locked: c.locked ?? false,
          // Carry author intent across promotion — motion timeline targets the
          // same cf-ids (now persisted), behavior script + theme stay attached.
          ...(f.themeId !== undefined ? { themeId: f.themeId } : {}),
          ...(f.script !== undefined ? { script: f.script } : {}),
          ...(f.motion !== undefined ? { motion: f.motion } : {}),
        };
        return main;
      });
    case 'main.setLocked':
      return mapFrames(s, (f) => {
        if (f.id !== c.id) return f;
        if (f.kind !== 'main') return f;
        return { ...f, locked: c.locked };
      });
    case 'instance.create': {
      const main = s.doc.frames.find((f) => f.id === c.mainId);
      if (!main || main.kind !== 'main') return s;
      const inst: InstanceFrame = {
        kind: 'instance',
        id: c.id,
        mainId: c.mainId,
        x: c.x, y: c.y,
        w: c.w ?? main.w,
        h: main.h,
        z: nextZ(s.doc.frames),
        title: main.title,
        effect: { kind: 'none' },
        slotValues: { ...c.slotValues },
      };
      return { ...s, doc: { ...s.doc, frames: [...s.doc.frames, inst] } };
    }
    case 'instance.setSlot':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'instance') return f;
        return { ...f, slotValues: { ...f.slotValues, [c.slotName]: c.value } };
      });
    case 'instance.setSlots':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'instance') return f;
        return { ...f, slotValues: { ...c.slotValues } };
      });
    case 'instance.detach': {
      const inst = s.doc.frames.find((f) => f.id === c.id);
      if (!inst || inst.kind !== 'instance') return s;
      const byId = framesById(s.doc.frames);
      const main = byId.get(inst.mainId);
      if (!main || main.kind !== 'main') return s;
      const resolvedHtml = resolveInstance(inst, byId);
      return mapFrames(s, (f) => {
        if (f.id !== c.id) return f;
        const free: FreeFrame = {
          kind: 'free',
          id: f.id, x: f.x, y: f.y, w: f.w, h: f.h, z: f.z,
          title: f.title, effect: f.effect,
          html: resolvedHtml,
        };
        return free;
      });
    }
    case 'frame.demoteFromMain': {
      const main = s.doc.frames.find((f) => f.id === c.id);
      if (!main || main.kind !== 'main') return s;
      const byId = framesById(s.doc.frames);
      // 1) detach every instance of this Main via recursive resolveInstance
      // 2) convert Main itself to FreeFrame
      const detached = s.doc.frames.map((f): Frame => {
        if (f.kind !== 'instance' || f.mainId !== c.id) return f;
        const free: FreeFrame = {
          kind: 'free',
          id: f.id, x: f.x, y: f.y, w: f.w, h: f.h, z: f.z,
          title: f.title, effect: f.effect,
          html: resolveInstance(f, byId),
        };
        return free;
      });
      return {
        ...s,
        doc: {
          ...s.doc,
          frames: detached.map((f): Frame => {
            if (f.id !== c.id) return f;
            const m = f as MainFrame;
            return {
              kind: 'free',
              id: m.id, x: m.x, y: m.y, w: m.w, h: m.h, z: m.z,
              title: m.title, effect: m.effect, html: m.html,
            };
          }),
        },
      };
    }
    case 'main.extractSubtree': {
      const main = s.doc.frames.find((f) => f.id === c.mainId);
      if (!main || main.kind !== 'main') return s;
      if (typeof document === 'undefined') return s;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = main.html;
      const host = document.createElement('div');
      host.appendChild(tmpl.content);
      const target = resolveCfNode(host, c.cfNodeId);
      if (!target) return s;
      const extractedHtml = injectCfNodeIds(target.outerHTML);
      const placeholder = document.createElement('cf-instance');
      placeholder.setAttribute('ref', c.newMainId);
      target.replaceWith(placeholder);
      const updatedMainHtml = host.innerHTML;
      const updatedMain: MainFrame = {
        ...main,
        html: updatedMainHtml,
        slots: extractSlotSchemas(updatedMainHtml),
      };
      const newMain: MainFrame = {
        kind: 'main',
        id: c.newMainId,
        x: c.x, y: c.y, w: 240, z: nextZ(s.doc.frames),
        title: c.title,
        effect: { kind: 'none' },
        html: extractedHtml,
        slots: extractSlotSchemas(extractedHtml),
        locked: false,
      };
      return {
        ...s,
        doc: {
          ...s.doc,
          frames: [
            ...s.doc.frames.map((f) => (f.id === main.id ? updatedMain : f)),
            newMain,
          ],
        },
      };
    }
    case 'canvas.batchSetSlot':
      return mapFrames(s, (f) => {
        if (f.kind !== 'instance' || f.mainId !== c.mainId) return f;
        if (!matchesPredicate(f, c.predicate)) return f;
        return { ...f, slotValues: { ...f.slotValues, [c.slotName]: c.value } };
      });
    case 'canvas.replaceInstances': {
      const to = s.doc.frames.find((f) => f.id === c.toMainId);
      if (!to || to.kind !== 'main') return s;
      return mapFrames(s, (f) => {
        if (f.kind !== 'instance' || f.mainId !== c.fromMainId) return f;
        const newSlotValues: { [k: string]: string } = {};
        for (const [from, toName] of Object.entries(c.slotMapping)) {
          const v = f.slotValues[from];
          if (v !== undefined) newSlotValues[toName] = v;
        }
        return { ...f, mainId: c.toMainId, slotValues: newSlotValues };
      });
    }
    case 'toolFrame.create': {
      const z = nextZ(s.doc.frames);
      const common = {
        id: c.id,
        x: c.x,
        y: c.y,
        z,
        anchorId: c.anchorId,
        effect: NONE_EFFECT,
      };
      let frame: Frame;
      if (c.toolKind === 'chat') {
        const chat: ChatFrame = {
          kind: 'chat',
          ...common,
          w: 240,
          // Fixed height so on-canvas chat scrolls internally instead of
          // growing unbounded as the conversation lengthens (panel chat ignores
          // h — it flexes to fill the rail).
          h: 360,
          title: c.anchorId ? 'chat → ' + c.anchorId : 'chat (global)',
          messages: [],
          draft: '',
          pinned: c.pinned ?? false,
          panelOwned: c.panelOwned,
        };
        frame = chat;
      } else if (c.toolKind === 'code') {
        const code: CodeFrame = {
          kind: 'code',
          ...common,
          w: 280,
          title: c.anchorId ? 'code → ' + c.anchorId : 'code (global)',
          panelOwned: c.panelOwned,
        };
        frame = code;
      } else if (c.toolKind === 'styleguide') {
        const sg: StyleguideFrame = {
          kind: 'styleguide',
          ...common,
          w: 320,
          title: 'styleguide',
        };
        frame = sg;
      } else {
        const fx: FxFrame = {
          kind: 'fx',
          ...common,
          w: 200,
          title: c.anchorId ? 'fx → ' + c.anchorId : 'fx (global)',
        };
        frame = fx;
      }
      return { ...s, doc: { ...s.doc, frames: [...s.doc.frames, frame] } };
    }
    case 'toolFrame.setAnchor':
      return mapFrames(s, (f) => {
        if (f.id !== c.id) return f;
        if (f.kind !== 'chat' && f.kind !== 'code' && f.kind !== 'fx') return f;
        return { ...f, anchorId: c.anchorId ?? undefined };
      });
    case 'toolFrame.pin':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'chat') return f;
        return { ...f, pinned: true };
      });
    case 'note.create': {
      const note: NoteFrame = {
        kind: 'note',
        id: c.id,
        x: c.x, y: c.y, w: c.w,
        z: nextZ(s.doc.frames),
        title: c.title,
        effect: NONE_EFFECT,
        html: c.html,
        target: c.target,
        tourId: c.tourId,
        step: c.step,
        color: c.color,
      };
      return { ...s, doc: { ...s.doc, frames: [...s.doc.frames, note] } };
    }
    case 'note.setBody':
      return mapFrames(s, (f) => (f.id === c.id && f.kind === 'note' ? { ...f, html: c.html } : f));
    case 'note.setTarget':
      return mapFrames(s, (f) => (f.id === c.id && f.kind === 'note' ? { ...f, target: c.target } : f));
    case 'note.setMeta':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'note') return f;
        return {
          ...f,
          title: c.title ?? f.title,
          tourId: c.tourId ?? f.tourId,
          step: c.step ?? f.step,
          color: c.color ?? f.color,
        };
      });
    case 'tour.upsert': {
      const tours = s.doc.tours ?? [];
      const exists = tours.some((t) => t.id === c.id);
      const nextTours = exists
        ? tours.map((t) => (t.id === c.id ? { id: c.id, title: c.title } : t))
        : [...tours, { id: c.id, title: c.title }];
      return { ...s, doc: { ...s.doc, tours: nextTours } };
    }
    case 'tour.remove': {
      const tours = (s.doc.tours ?? []).filter((t) => t.id !== c.id);
      return { ...s, doc: { ...s.doc, tours } };
    }
    case 'chat.appendMessage':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'chat') return f;
        return { ...f, messages: [...f.messages, c.message] };
      });
    case 'chat.patchMessage':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'chat') return f;
        return {
          ...f,
          messages: f.messages.map((m) => (m.id === c.messageId ? { ...m, text: c.text } : m)),
        };
      });
    case 'chat.setDraft':
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'chat') return f;
        return { ...f, draft: c.draft };
      });
    case 'chat.clear':
      // Start a fresh conversation: wipe the visible transcript + draft. The
      // server-side agent memory is reset separately (reset protocol message).
      return mapFrames(s, (f) => {
        if (f.id !== c.id || f.kind !== 'chat') return f;
        return { ...f, messages: [], draft: '' };
      });
    case 'motion.init': {
      const withMotion = patchMotion(s, c.frameId, (tl) => tl);
      // Freeze cf-ids into a free frame's html the moment it gains a timeline,
      // so picked targets reference stable ids (main already persists them;
      // tsx has no html). injectCfNodeIds keeps any pre-existing ids.
      return mapFrames(withMotion, (f) =>
        f.id === c.frameId && f.kind === 'free' ? { ...f, html: injectCfNodeIds(f.html) } : f,
      );
    }
    case 'motion.addTrack':
      return patchMotion(s, c.frameId, (tl) => {
        const track: MotionTrack = { id: c.trackId, target: c.target, label: c.label, layers: [{ id: c.layerId, blend: 'replace', channels: [] }] };
        return { ...tl, tracks: [...tl.tracks, track] };
      });
    case 'motion.removeTrack':
      return patchMotion(s, c.frameId, (tl) => mRemoveTrack(tl, c.trackId));
    case 'motion.addLayer':
      return patchMotion(s, c.frameId, (tl) => ({
        ...tl,
        tracks: tl.tracks.map((t) => (t.id === c.trackId ? { ...t, layers: [...t.layers, { id: c.layerId, blend: c.blend, channels: [] }] } : t)),
      }));
    case 'motion.setLayerBlend':
      return patchMotion(s, c.frameId, (tl) => mSetLayerBlend(tl, c.trackId, c.layerId, c.blend));
    case 'motion.setKeyframe':
      return patchMotion(s, c.frameId, (tl) => mSetKeyframe(tl, { trackId: c.trackId, layerId: c.layerId, prop: c.prop, t: c.kf.t, value: c.kf.value, ease: c.kf.ease }));
    case 'motion.moveKeyframe':
      return patchMotion(s, c.frameId, (tl) => mMoveKeyframe(tl, { trackId: c.trackId, layerId: c.layerId, prop: c.prop, fromT: c.fromT, toT: c.toT }));
    case 'motion.removeKeyframe':
      return patchMotion(s, c.frameId, (tl) => mRemoveKeyframe(tl, { trackId: c.trackId, layerId: c.layerId, prop: c.prop, t: c.atT }));
    case 'motion.setEase':
      return patchMotion(s, c.frameId, (tl) => mSetEase(tl, { trackId: c.trackId, layerId: c.layerId, prop: c.prop, t: c.kfT, ease: c.ease }));
    case 'motion.setDuration':
      return patchMotion(s, c.frameId, (tl) => mSetDuration(tl, c.duration));
    case 'motion.setPlayback':
      return patchMotion(s, c.frameId, (tl) => mSetPlayback(tl, c.playback));

    case 'comment.create': {
      const comments = s.doc.comments ?? [];
      const thread = { id: c.id, target: c.target, resolved: false, messages: [c.message] };
      return { ...s, doc: { ...s.doc, comments: [...comments, thread] } };
    }
    case 'comment.reply': {
      const comments = s.doc.comments ?? [];
      return {
        ...s,
        doc: {
          ...s.doc,
          comments: comments.map((t) =>
            t.id === c.threadId ? { ...t, messages: [...t.messages, c.message] } : t,
          ),
        },
      };
    }
    case 'comment.resolve': {
      const comments = s.doc.comments ?? [];
      return {
        ...s,
        doc: {
          ...s.doc,
          comments: comments.map((t) => (t.id === c.threadId ? { ...t, resolved: c.resolved } : t)),
        },
      };
    }
    case 'comment.delete': {
      const comments = s.doc.comments ?? [];
      return { ...s, doc: { ...s.doc, comments: comments.filter((t) => t.id !== c.threadId) } };
    }
  }
}

function mapFrames(s: CanvasState, fn: (f: Frame) => Frame): CanvasState {
  return { ...s, doc: { ...s.doc, frames: s.doc.frames.map(fn) } };
}

const DESIGN_KINDS = new Set(['free', 'main', 'tsxFree', 'tsxMain']);
function patchMotion(s: CanvasState, frameId: string, fn: (tl: MotionTimeline) => MotionTimeline): CanvasState {
  return mapFrames(s, (f) => {
    if (f.id !== frameId || !DESIGN_KINDS.has(f.kind)) return f;
    const tl = ('motion' in f && f.motion) ? f.motion : emptyTimeline();
    return { ...f, motion: fn(tl) } as Frame;
  });
}

const MODE_GROUPS = ['colors', 'typography', 'rounded', 'spacing', 'components', 'shadow', 'motion'] as const;

function mergeMode(base: Partial<ThemeModeTokens>, patch: Partial<ThemeModeTokens>): Partial<ThemeModeTokens> {
  const out = { ...base, ...patch } as Partial<ThemeModeTokens>;
  for (const g of MODE_GROUPS) {
    const pg = patch[g];
    if (pg && typeof pg === 'object') {
      (out as unknown as Record<string, unknown>)[g] = { ...((((base as unknown) as Record<string, unknown>)[g] as object) ?? {}), ...(pg as object) };
    }
  }
  return out;
}

export function mergeTheme(base: ThemeToken, patch: Partial<ThemeToken>): ThemeToken {
  const out = { ...base, ...patch } as ThemeToken;
  for (const g of MODE_GROUPS) {
    const pg = patch[g];
    if (pg && typeof pg === 'object') {
      (out as unknown as Record<string, unknown>)[g] = { ...((((base as unknown) as Record<string, unknown>)[g] as object) ?? {}), ...(pg as object) };
    }
  }
  if (patch.dark) {
    out.dark = mergeMode(base.dark ?? lightTokens(base), patch.dark);
  }
  return out;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}


function matchesPredicate(f: InstanceFrame, p?: import('./types').BatchPredicate): boolean {
  if (!p) return true;
  if (p.slotName !== undefined && p.slotEquals !== undefined) {
    if (f.slotValues[p.slotName] !== p.slotEquals) return false;
  }
  if (p.inRect) {
    const r = p.inRect;
    const h = f.h ?? 0;
    if (f.x + f.w < r.x || f.x > r.x + r.w || f.y + h < r.y || f.y > r.y + r.h) return false;
  }
  return true;
}

/** Helper: get displayable html for any Frame variant.
 *  Delegates to resolveInstance which handles free / main / instance + cycle
 *  detection + nested cf-instance expansion. */
export function getDisplayHtml(f: Frame, byId: Map<string, Frame>): string {
  return isolateStyles(resolveInstance(f, byId));
}

/**
 * Display HTML for the RENDER seam — same as getDisplayHtml but with stable
 * data-cf-id stamped on every element. Layer-selection (resolveLayerAt walking
 * the live DOM, LayerOverlay breadcrumb, comment subtree reads) requires cf-ids
 * to exist regardless of how the frame's html was produced (agent-created html
 * is injected at frame.create, but seeded / hand-authored / edited html is not).
 * injectCfNodeIds is deterministic by document order, so the renderer and the
 * overlay derive identical ids from identical html. Kept out of getDisplayHtml
 * so export/agent/tailwind consumers stay free of cf-id noise.
 */
export function getRenderHtml(f: Frame, byId: Map<string, Frame>): string {
  return injectCfNodeIds(getDisplayHtml(f, byId));
}

/** Build an id-to-frame map. Cheap helper for reducers that need byId. */
export function framesById(frames: Frame[]): Map<string, Frame> {
  return new Map(frames.map((f) => [f.id, f] as const));
}

// keep referenced for type narrowing helpers re-exported elsewhere
export type _Variants = FreeFrame | MainFrame | InstanceFrame;
